/**
 * 🔐 SECURE Brainiacs Corporate API Service
 *
 * This service provides a secure interface to call the Brainiacs corporate server
 * via an Appwrite Function proxy that handles authentication and bearer token management.
 *
 * Security Features:
 * - Automatic Appwrite Authentication via SDK
 * - Bearer token never exposed to client (server-side only)
 * - Type-safe API methods
 * - Error handling and logging
 *
 * Usage:
 * ```typescript
 * import { brainiacsService } from '@/components/core/services/brainiacs.service';
 *
 * const result = await brainiacsService.getUserData({ userId: '123' });
 * if (result.success) {
 *   console.log(result.data);
 * }
 * ```
 */

import { Client, Functions, ExecutionMethod } from "appwrite";

/**
 * Base types for Brainiacs Corporate API
 */
export interface BrainiacsApiRequest {
  action: string;
  parameters?: Record<string, any>;
}

export interface BrainiacsApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  timestamp: string;
}

/**
 * Action-specific parameter types
 */
export interface GetUserDataParams {
  userId?: string;
}

export interface CreateCampaignParams {
  name: string;
  targetAudience: string;
  budget: number;
  description?: string;
  startDate?: string;
  endDate?: string;
}

export interface UpdateProfileParams {
  userId?: string;
  profileData: Record<string, any>;
}

export interface FetchAnalyticsParams {
  campaignId?: string;
  dateFrom?: string;
  dateTo?: string;
  metrics?: string[];
}

export interface GetCampaignStatusParams {
  campaignId: string;
}

export interface ListCampaignsParams {
  limit?: number;
  offset?: number;
  status?: "active" | "paused" | "completed";
}

export interface UpdateCampaignParams {
  campaignId: string;
  updates: Record<string, any>;
}

export interface DeleteCampaignParams {
  campaignId: string;
}

export interface GetCreditBalanceParams {
  userId?: string;
  /** Override the user_email sent to the API. Credits are team-wide, so this
   *  is "any member of the team whose balance you want": the admin dashboard
   *  passes one member per team; impersonation passes the target's email. */
  userEmail?: string;
}

export interface GetStripeCheckoutUrlParams {
  amount: number;
  currency?: string;
  successUrl?: string;
  cancelUrl?: string;
}

export interface GetInvoicesParams {
  limit?: number;
  status?: "draft" | "open" | "paid" | "uncollectible" | "void";
}

/**
 * Brainiacs Corporate API Service
 */
export class BrainiacsService {
  private appwriteFunctionId: string;
  private client: Client;
  private functions: Functions;

  constructor() {
    // Initialize Appwrite client (same pattern as n8n.service.ts)
    this.client = new Client()
      .setEndpoint(import.meta.env.VITE_APPWRITE_ENDPOINT || "")
      .setProject(import.meta.env.VITE_APPWRITE_PROJECT_ID || "");

    this.functions = new Functions(this.client);
    this.appwriteFunctionId = "n8n-proxy";
  }

  /**
   * Generic method to call Brainiacs Corporate API via Appwrite SDK
   * 🔐 SECURE: Uses Appwrite SDK which handles auth automatically (no CORS issues)
   *
   * @private
   */
  /**
   * Read-only actions safe to retry on the phantom-execution pattern.
   * Self-hosted Appwrite occasionally returns a 200 with an EMPTY body;
   * retrying an idempotent read once is safe. Mirrors n8n.service.ts.
   */
  private static readonly RETRYABLE_READ_ACTIONS = new Set([
    "get_credit_balance",
    "get_user_data",
    "get_stripe_checkout_url_for_topup",
  ]);

  private async callApi<T = any>(
    action: string,
    parameters?: Record<string, any>,
  ): Promise<BrainiacsApiResponse<T>> {
    const isRetryableRead = BrainiacsService.RETRYABLE_READ_ACTIONS.has(action);
    try {
      return await this.executeCallApi<T>(action, parameters, isRetryableRead);
    } catch (error: any) {
      // Retry once on the phantom empty-body pattern (read actions only).
      if (isRetryableRead && error?.message?.includes("No response body from Appwrite function")) {
        console.warn("🔁 Empty execution body — retrying read action once:", action);
        return this.executeCallApi<T>(action, parameters, false);
      }
      // executeCallApi throws for retryable reads so we get the retry chance;
      // for non-retryable actions it returns a success:false envelope.
      if (error?.message?.includes("No response body from Appwrite function")) {
        return {
          success: false,
          error: error.message,
          timestamp: new Date().toISOString(),
        };
      }
      throw error;
    }
  }

  private async executeCallApi<T = any>(
    action: string,
    parameters: Record<string, any> | undefined,
    throwOnEmptyBody: boolean,
  ): Promise<BrainiacsApiResponse<T>> {
    try {
      // Get current user for email
      const { backendProvider } = await import("../../../services/backend");
      const user = backendProvider.auth.getCurrentUser();

      if (!user) {
        throw new Error("User must be authenticated");
      }

      console.log(`\n🔵 ========================================`);
      console.log(
        `📡 Brainiacs Corporate API Request (via Appwrite SDK)`,
      );
      console.log(`========================================`);
      console.log("🎯 Action:", action);
      console.log("📦 Parameters:", parameters);
      console.log("👤 User:", user.email);
      console.log(`========================================\n`);

      // IMPORTANT: Consistent webhook structure - action, user_email, parameters
      const targetEmail = backendProvider.auth.getTargetEmail();
      // Allow the caller to override user_email (e.g. credit balance should
      // always use the authenticated user, not the impersonated target).
      const effectiveEmail = parameters?.userEmail || targetEmail || user.email || "";
      const requestBody = {
        action,
        user_email: effectiveEmail,
        parameters: parameters || {},
      };

      console.log("📤 Sending request action:", action);

      // Use Appwrite SDK to execute function (same pattern as n8n.service.ts)
      // The SDK properly formats the request and handles auth - no CORS issues
      const execution = await this.functions.createExecution(
        this.appwriteFunctionId,
        JSON.stringify(requestBody), // Data parameter - sent as string
        false, // Not async
        "/", // Path
        ExecutionMethod.POST, // Method
      );

      console.log(`\n🟢 ========================================`);
      console.log(`📥 Brainiacs Corporate API Response`);
      console.log(`========================================`);
      console.log("📦 Appwrite execution status:", execution?.responseStatusCode);

      // Extract responseBody (contains actual N8N data as JSON string)
      const responseBody = execution.responseBody;
      if (!responseBody) {
        // For retryable reads, throw so callApi can retry once. For writes,
        // return the success:false envelope so the caller sees the failure.
        if (throwOnEmptyBody) {
          throw new Error("No response body from Appwrite function");
        }
        return {
          success: false,
          error: "No response body from Appwrite function",
          timestamp: new Date().toISOString(),
        };
      }

      // Parse the response from responseBody string
      const responseData = JSON.parse(responseBody);
      console.log(
        "📦 Response: success=%s, data keys=%s",
        responseData.success,
        responseData.data ? Object.keys(responseData.data).join(",") : "none",
      );
      console.log(`========================================\n`);

      // Check if proxy returned success
      if (!responseData.success) {
        throw new Error(
          responseData.message ||
            responseData.error ||
            "API returned failure",
        );
      }

      console.log("✅ Brainiacs Corporate API SUCCESS!");
      console.log("💰 Data received:", responseData.data);

      return {
        success: true,
        data: responseData.data,
        timestamp: responseData.timestamp || new Date().toISOString(),
      };
    } catch (error: any) {
      console.log(`\n🔴 ========================================`);
      console.error("❌ Brainiacs Corporate API ERROR");
      console.log(`========================================`);
      console.error("🎯 Action:", action);
      console.error("📦 Parameters:", parameters);
      console.error("⚠️ Error Message:", error.message);
      console.error("📋 Full Error:", error);
      console.log(`========================================\n`);

      // For retryable reads, re-throw so callApi gets the retry chance.
      if (throwOnEmptyBody) throw error;

      return {
        success: false,
        error: error.message || "Unknown error",
        timestamp: new Date().toISOString(),
      };
    }
  }

  /**
   * Get user data from Brainiacs corporate server
   *
   * @param params - Optional userId (defaults to current user)
   * @returns User data response
   *
   * @example
   * ```typescript
   * const result = await brainiacsService.getUserData({ userId: '123' });
   * if (result.success) {
   *   console.log('User:', result.data);
   * }
   * ```
   */
  async getUserData(params?: GetUserDataParams): Promise<BrainiacsApiResponse> {
    return this.callApi("get_user_data", params);
  }

  /**
   * Get user's credit balance from Brainiacs corporate server
   *
   * @param params - Optional userId (defaults to current user)
   * @returns Credit balance response
   *
   * @example
   * ```typescript
   * const result = await brainiacsService.getCreditBalance();
   * if (result.success) {
   *   console.log('Credits:', result.data.balance);
   * }
   * ```
   */
  async getCreditBalance(
    params?: GetCreditBalanceParams,
  ): Promise<BrainiacsApiResponse> {
    // Strip the userEmail override from the parameters object before sending
    // — it's consumed by callApi for the user_email field, not sent to N8N.
    const { userEmail, ...rest } = params || {};
    return this.callApi("get_credit_balance", { ...rest, userEmail } as any);
  }

  /**
   * Get Stripe checkout URL for credit top-up
   *
   * @param params - Top-up amount and optional URLs
   * @returns Stripe checkout URL response
   *
   * @example
   * ```typescript
   * const result = await brainiacsService.getStripeCheckoutUrl({
   *   amount: 100, // $100 credit top-up
   *   currency: 'usd',
   *   successUrl: 'https://app.brainiacs.com/success',
   *   cancelUrl: 'https://app.brainiacs.com/cancel'
   * });
   * if (result.success) {
   *   window.location.href = result.data.checkoutUrl;
   * }
   * ```
   */
  async getStripeCheckoutUrl(
    params: GetStripeCheckoutUrlParams,
  ): Promise<BrainiacsApiResponse> {
    return this.callApi("get_stripe_checkout_url_for_topup", params);
  }

  /**
   * Create a new campaign on Brainiacs corporate server
   *
   * @param params - Campaign creation parameters
   * @returns Campaign creation response
   *
   * @example
   * ```typescript
   * const result = await brainiacsService.createCampaign({
   *   name: 'Q1 2025 Campaign',
   *   targetAudience: 'Tech startups in SF',
   *   budget: 50000,
   *   description: 'Lead generation for tech sector'
   * });
   * ```
   */
  async createCampaign(
    params: CreateCampaignParams,
  ): Promise<BrainiacsApiResponse> {
    return this.callApi("create_campaign", params);
  }

  /**
   * Update user profile on Brainiacs corporate server
   *
   * @param params - Profile update parameters
   * @returns Profile update response
   *
   * @example
   * ```typescript
   * const result = await brainiacsService.updateProfile({
   *   profileData: {
   *     company: 'Acme Inc',
   *     role: 'Marketing Director'
   *   }
   * });
   * ```
   */
  async updateProfile(
    params: UpdateProfileParams,
  ): Promise<BrainiacsApiResponse> {
    return this.callApi("update_profile", params);
  }

  /**
   * Fetch analytics data from Brainiacs corporate server
   *
   * @param params - Analytics query parameters
   * @returns Analytics data response
   *
   * @example
   * ```typescript
   * const result = await brainiacsService.fetchAnalytics({
   *   campaignId: 'camp_123',
   *   dateFrom: '2025-01-01',
   *   dateTo: '2025-01-31',
   *   metrics: ['impressions', 'clicks', 'conversions']
   * });
   * ```
   */
  async fetchAnalytics(
    params?: FetchAnalyticsParams,
  ): Promise<BrainiacsApiResponse> {
    return this.callApi("fetch_analytics", params);
  }

  /**
   * Get campaign status from Brainiacs corporate server
   *
   * @param params - Campaign ID to query
   * @returns Campaign status response
   *
   * @example
   * ```typescript
   * const result = await brainiacsService.getCampaignStatus({ campaignId: 'camp_123' });
   * if (result.success) {
   *   console.log('Status:', result.data.status);
   * }
   * ```
   */
  async getCampaignStatus(
    params: GetCampaignStatusParams,
  ): Promise<BrainiacsApiResponse> {
    return this.callApi("get_campaign_status", params);
  }

  /**
   * List campaigns from Brainiacs corporate server
   *
   * @param params - List filters and pagination
   * @returns List of campaigns
   *
   * @example
   * ```typescript
   * const result = await brainiacsService.listCampaigns({
   *   limit: 50,
   *   status: 'active'
   * });
   * ```
   */
  async listCampaigns(
    params?: ListCampaignsParams,
  ): Promise<BrainiacsApiResponse> {
    return this.callApi("list_campaigns", params);
  }

  /**
   * Update campaign on Brainiacs corporate server
   *
   * @param params - Campaign ID and updates
   * @returns Update response
   *
   * @example
   * ```typescript
   * const result = await brainiacsService.updateCampaign({
   *   campaignId: 'camp_123',
   *   updates: {
   *     status: 'paused',
   *     budget: 75000
   *   }
   * });
   * ```
   */
  async updateCampaign(
    params: UpdateCampaignParams,
  ): Promise<BrainiacsApiResponse> {
    return this.callApi("update_campaign", params);
  }

  /**
   * Delete campaign on Brainiacs corporate server
   *
   * @param params - Campaign ID to delete
   * @returns Deletion response
   *
   * @example
   * ```typescript
   * const result = await brainiacsService.deleteCampaign({ campaignId: 'camp_123' });
   * ```
   */
  async deleteCampaign(
    params: DeleteCampaignParams,
  ): Promise<BrainiacsApiResponse> {
    return this.callApi("delete_campaign", params);
  }

  /**
   * Get client invoices from Stripe via Brainiacs corporate server
   *
   * @param params - Optional filters (limit, status)
   * @returns List of invoices
   *
   * @example
   * ```typescript
   * const result = await brainiacsService.getInvoices({
   *   limit: 10,
   *   status: 'paid'
   * });
   * if (result.success) {
   *   console.log('Invoices:', result.data);
   * }
   * ```
   */
  async getInvoices(params?: GetInvoicesParams): Promise<BrainiacsApiResponse> {
    return this.callApi("get_invoices", params);
  }

  /**
   * Generic method for custom actions not yet implemented
   *
   * @param action - Action name (must be in whitelist on server)
   * @param parameters - Action-specific parameters
   * @returns API response
   *
   * @example
   * ```typescript
   * const result = await brainiacsService.customAction('custom_action', {
   *   param1: 'value1',
   *   param2: 'value2'
   * });
   * ```
   */
  async customAction(
    action: string,
    parameters?: Record<string, any>,
  ): Promise<BrainiacsApiResponse> {
    return this.callApi(action, parameters);
  }
}

/**
 * Singleton instance for easy importing
 *
 * @example
 * ```typescript
 * import { brainiacsService } from '@/components/core/services/brainiacs.service';
 *
 * const result = await brainiacsService.getUserData();
 * ```
 */
export const brainiacsService = new BrainiacsService();
